/**
 * v1.5 P0 B6：系统监控 tab
 *
 * admin 看：cron 健康度 / AI 消耗 / SMS 用量 / 今日活跃
 * 只读，每 30s 刷新
 */
import { Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { Pill } from '../../components/ui';
import { fmtTime, fmtMonthDayTime } from '../../lib/format';

type MonitorData = {
  ai: {
    total_cost_cny: number; total_calls: number; failed: number; success_rate: number;
    by_feature: Array<{ feature: string; calls: number; cost: number; tokens: number; ok: number; failed: number }>;
  };
  sms: { total_sent: number; by_purpose: Array<{ purpose: string; sent: number }> };
  activity_today: { active_users: number; follow_ups: number; orders: number };
  cron_health: Record<string, { last_at: string | null; stale: boolean }>;
  checked_at: string;
};

export function SystemMonitor() {
  const { data, isLoading } = useQuery<MonitorData>({
    queryKey: ['admin-monitor'],
    queryFn: () => $.get('/admin/system-monitor/dashboard'),
    refetchInterval: 30_000,
  });

  if (isLoading || !data) return <div style={{ padding: 40, textAlign: 'center', color: '#8F959E' }}>加载中…</div>;

  return (
    <div>
      <div style={{ fontSize: 11, color: '#8F959E', marginBottom: 12, textAlign: 'right' }}>
        最后检查 {fmtTime(data.checked_at)} · 30s 自动刷新
      </div>

      {/* Cron 健康度 */}
      <Section title="⏰ Cron 健康度">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <CronCard name="公海回收 cron" cron="每天 02:00"
                    lastAt={data.cron_health.pool_recycle?.last_at}
                    stale={data.cron_health.pool_recycle?.stale} />
          <CronCard name="健康度重算 cron" cron="每天 03:00（v1.5 新增）"
                    lastAt={data.cron_health.health_recompute?.last_at}
                    stale={data.cron_health.health_recompute?.stale} />
          <CronCard name="AI 调用" cron="多种 cron 触发"
                    lastAt={data.cron_health.ai_calls?.last_at}
                    stale={data.cron_health.ai_calls?.stale} />
          {/* bug收尾批#7：补薪酬/绩效三条月度 cron(每月 1 号；后端按月度节奏判 stale，超 33 天=漏跑上月) */}
          <CronCard name="薪酬定级 cron" cron="每月 1 号 04:00"
                    lastAt={data.cron_health.salary_grade?.last_at}
                    stale={data.cron_health.salary_grade?.stale} />
          <CronCard name="发薪台账 cron" cron="每月 1 号 05:00"
                    lastAt={data.cron_health.payroll_ledger?.last_at}
                    stale={data.cron_health.payroll_ledger?.stale} />
          <CronCard name="员工绩效 cron" cron="每月 1 号 02:00"
                    lastAt={data.cron_health.monthly_performance?.last_at}
                    stale={data.cron_health.monthly_performance?.stale} />
        </div>
      </Section>

      {/* AI 月度 */}
      <Section title="🤖 AI 月度消耗">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
          <Stat label="月度费用" value={`¥${data.ai.total_cost_cny.toFixed(2)}`} tone={data.ai.total_cost_cny > 500 ? 'warning' : 'normal'} />
          <Stat label="月度调用" value={`${data.ai.total_calls} 次`} />
          <Stat label="成功率" value={`${data.ai.success_rate}%`} tone={data.ai.success_rate < 90 ? 'danger' : data.ai.success_rate < 95 ? 'warning' : 'success'} />
          <Stat label="失败次数" value={`${data.ai.failed}`} tone={data.ai.failed > 50 ? 'danger' : data.ai.failed > 10 ? 'warning' : 'normal'} />
        </div>
        <table style={{ width: '100%', fontSize: 12 }}>
          <thead style={{ background: '#F5F6F7', color: '#646A73' }}>
            <tr><th style={{ padding: 8, textAlign: 'left' }}>功能</th><th style={{ padding: 8, textAlign: 'right' }}>调用</th><th style={{ padding: 8, textAlign: 'right' }}>费用</th><th style={{ padding: 8, textAlign: 'right' }}>Token</th><th style={{ padding: 8, textAlign: 'right' }}>成功/失败</th></tr>
          </thead>
          <tbody>
            {data.ai.by_feature.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 20, color: '#8F959E', textAlign: 'center' }}>本月无 AI 调用 ⚠️（请确认 AI 配置正常）</td></tr>
            ) : data.ai.by_feature.map((r) => (
              <tr key={r.feature} style={{ borderTop: '1px solid #F0F2F5' }}>
                <td style={{ padding: 8, fontFamily: 'monospace' }}>{r.feature}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{r.calls}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>¥{r.cost.toFixed(2)}</td>
                <td style={{ padding: 8, textAlign: 'right', color: '#8F959E' }}>{r.tokens.toLocaleString()}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>
                  <span style={{ color: 'var(--success)' }}>{r.ok}</span>
                  {r.failed > 0 && <span style={{ color: 'var(--danger)' }}>{' / '}{r.failed}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* SMS */}
      <Section title="📱 SMS 月度">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Stat label="月度发送" value={`${data.sms.total_sent} 条`} />
          <div style={{ flex: 1, display: 'flex', gap: 8 }}>
            {data.sms.by_purpose.map((r) => (
              <Tag key={r.purpose}>{r.purpose}: {r.sent}</Tag>
            ))}
          </div>
        </div>
        {data.sms.total_sent > 1000 && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--warning)' }}>
            ⚠️ 月度发送超 1000 条，注意阿里云余额
          </div>
        )}
      </Section>

      {/* 活跃度 */}
      <Section title="📊 今日活跃">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Stat label="今日活跃用户" value={`${data.activity_today.active_users}`} />
          <Stat label="今日跟进数" value={`${data.activity_today.follow_ups}`} />
          <Stat label="今日新订单" value={`${data.activity_today.orders}`} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'normal' | 'warning' | 'danger' | 'success' }) {
  const color = tone === 'danger' ? 'var(--danger)' : tone === 'warning' ? 'var(--warning)' : tone === 'success' ? 'var(--success)' : '#1F2329';
  return (
    <div style={{ padding: 12, background: '#F5F6F7', borderRadius: 6, textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#8F959E', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color }}>{value}</div>
    </div>
  );
}

function CronCard({ name, cron, lastAt, stale }: { name: string; cron: string; lastAt: string | null; stale: boolean }) {
  return (
    <div style={{
      padding: 12, borderRadius: 8,
      border: '1px solid ' + (stale ? 'var(--danger)' : '#DEE0E3'),
      background: stale ? '#FEEBEC' : '#FFFFFF',
    }}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
      <div style={{ fontSize: 11, color: '#8F959E', marginTop: 4 }}>{cron}</div>
      <div style={{ fontSize: 12, marginTop: 8 }}>
        {lastAt
          ? <>上次：{fmtMonthDayTime(lastAt)} {stale && <Pill tone="danger">⚠️ 超时</Pill>}</>
          : <Pill tone="warning">⚠️ 从未跑</Pill>}
      </div>
    </div>
  );
}
